import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { fmtPrice, cn } from '../../../../lib/utils';
import { useLtpStore } from '../../../../store/ltpStore';
import { manualOrderTypeLabel } from '../manualLabels';

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50 last:border-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

const decPlaces = (s) => { const str = String(s ?? ''); const i = str.indexOf('.'); return i < 0 ? 0 : str.length - i - 1; };
// Tick-multiple check via integer scaling — avoids binary-float modulo errors.
function isTickMultiple(priceStr, tickStr) {
  const p = Number(priceStr); const t = Number(tickStr);
  if (!Number.isFinite(p) || !Number.isFinite(t) || p <= 0 || t <= 0) return false;
  const scale = Math.pow(10, Math.max(decPlaces(priceStr), decPlaces(tickStr)));
  const pi = Math.round(p * scale); const ti = Math.round(t * scale);
  return ti > 0 && pi % ti === 0;
}

// ctx: { prepared }, where `prepared` is the /exit/prepare response. Mirrors the LIVE entry confirm modal:
// Trigger Price is editable (tick-validated), Quantity is read-only, LTP updates live, and a backend-driven
// countdown gates Confirm. The exit dispatches a protected LIMIT; the server re-checks the broker position.
export default function ConfirmManualExitModal({ ctx, submitting = false, error = null, onConfirm, onCancel, onExpire }) {
  const prepared = ctx?.prepared || null;
  const [trigger, setTrigger] = useState(() => (prepared?.suggested_limit_price ?? ''));
  const [nowMs, setNowMs] = useState(() => Date.now());
  const liveLtpMap = useLtpStore((s) => s.ltp);

  const expiresMs = prepared ? Date.parse(prepared.expires_at) : null;
  // Countdown ticks. Cleared on unmount/close → no timer leaks.
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(id);
  }, []);
  // On expiry, hand off to the parent (it clears prepared state before closing).
  useEffect(() => {
    if (expiresMs != null && nowMs >= expiresMs) onExpire?.();
  }, [nowMs, expiresMs, onExpire]);

  if (!prepared) return null;
  const remainingSec = expiresMs != null ? Math.max(0, Math.ceil((expiresMs - nowMs) / 1000)) : null;
  const expired = remainingSec === 0;
  const side = prepared.exit_action; // 'BUY' | 'SELL'
  const isBuy = side === 'BUY';

  const tickValid = isTickMultiple(trigger, prepared.tick_size);
  const edited = String(trigger) !== String(prepared.suggested_limit_price);
  const normSym = String(prepared.symbol || '').trim().toUpperCase();
  const liveLtp = liveLtpMap[normSym] ?? prepared.ltp;

  const confirmDisabled = submitting || expired || !tickValid;
  const handleConfirm = () => {
    if (confirmDisabled) return;
    onConfirm(String(trigger), edited ? 'MANUAL_OVERRIDE' : 'AUTO_PROTECTED');
  };

  const body = (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-txt-primary">
          Confirm Manual <span className={isBuy ? 'text-bull' : 'text-bear'}>{side}</span>
        </h3>
        <span className="badge border text-2xs bg-bear/10 border-bear/30 text-bear">LIVE</span>
      </div>

      <div className="mb-4">
        <Field label="Symbol" value={prepared.display_symbol || prepared.symbol} />
        <Field label="Side" value={side} />
        <Field label="LTP" value={fmtPrice(liveLtp)} />
        <Field label="MPP" value={`${prepared.market_protection_pct}%`} />
        <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50">
          <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Trigger Price</span>
          <input
            type="number"
            inputMode="decimal"
            value={trigger}
            onChange={(e) => setTrigger(e.target.value)}
            className={cn('input-field !w-32 text-right text-base !h-11 lg:!h-7 lg:text-xs', !tickValid && 'border-bear/60')}
          />
        </div>
        {!tickValid && (
          <p className="text-2xs font-mono text-bear text-right pt-1">Must be a positive multiple of tick {prepared.tick_size}</p>
        )}
        <Field label="Quantity" value={prepared.open_quantity} />
        <Field label="Order Type" value={manualOrderTypeLabel(prepared.effective_order_type)} />
        <Field
          label="Confirm In"
          value={<span className={cn('font-medium', expired ? 'text-bear' : remainingSec <= 10 ? 'text-warn' : 'text-txt-primary')}>{remainingSec}s</span>}
        />
      </div>

      {error && (
        <div className="rounded-md border border-bear/40 bg-bear/10 px-3 py-2.5 mb-4">
          <p className="text-xs font-mono text-bear break-words">{error}</p>
        </div>
      )}

      {/* Mobile (<lg): sticky footer within the BottomSheet's scrollable body, matching
          the entry-signal confirm sheet's idiom. lg: resets restore the desktop layout. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="btn-ghost disabled:opacity-50 min-h-[48px] lg:min-h-0"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={confirmDisabled}
          className={cn(
            'font-semibold text-sm px-4 py-2 rounded-md border transition-colors disabled:opacity-60 disabled:cursor-not-allowed min-h-[48px] lg:min-h-0',
            isBuy
              ? 'bg-bull/15 border-bull/40 text-bull hover:bg-bull/25'
              : 'bg-bear/15 border-bear/40 text-bear hover:bg-bear/25',
          )}
        >
          {submitting ? 'Submitting LIVE order…' : `Confirm ${side}`}
        </button>
      </div>
    </>
  );

  return createPortal(
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-[1100] items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-md max-h-[90vh] overflow-y-auto animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. */}
      <BottomSheet open onClose={onCancel} title="">
        {body}
      </BottomSheet>
    </>,
    document.body,
  );
}
